import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Send, Check, X, ArrowRightCircle, Trash2, Pencil, Eye, FileDown, Lock } from "lucide-react";
import { fmtCurrency, fmtDate, STATUS_TR, quotePdfName } from "@/utils/format";
import QuoteDetailsDialog from "@/components/QuoteDetailsDialog";
import PageHeader from "@/components/PageHeader";
import Pager, { usePagination } from "@/components/Pager";
import { SimilarQuoteBadge, SimilarQuotesDialog } from "@/components/SimilarQuotes";

const STATUS_COLORS = {
  draft: "bg-neutral-200 text-neutral-800",
  sent: "bg-yellow-100 text-yellow-800 border-yellow-800",
  approved: "bg-green-100 text-green-800 border-green-800",
  rejected: "bg-red-100 text-red-800 border-red-800",
  converted: "bg-[#0055FF] text-white",
};

export default function Quotes() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [compareId, setCompareId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isElevated = user?.role === "admin" || user?.role === "sub_admin";
  const canManageQuote = (r) => isElevated || (r?.created_by || "").toLowerCase() === (user?.email || "").toLowerCase();

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/quotes?_ts=${Date.now()}`);
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/quotes/${id}/status?status=${status}`); toast.success("Durum güncellendi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const convert = async (id, itemIds) => {
    const partial = Array.isArray(itemIds) && itemIds.length > 0;
    const msg = partial
      ? `Seçili ${itemIds.length} kalemi siparişe dönüştürmek istiyor musunuz? Diğer kalemler teklifte beklemede kalacak.`
      : "Bu teklifi siparişe dönüştürmek istiyor musunuz? Stok durumu kontrol edilerek Depo veya Üretim'e sevk edilecek.";
    if (!window.confirm(msg)) return;
    try {
      const body = partial ? { item_ids: itemIds } : {};
      const { data } = await api.post(`/quotes/${id}/convert`, body);
      toast.success(`Sipariş oluşturuldu: ${data.order_number}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Teklifi silmek istiyor musunuz?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/quotes/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = quotePdfName(r); a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = rows.filter((r) =>
    (r.quote_number || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.customer_name || "").toLowerCase().includes(q.toLowerCase())
  );
  const { page, setPage, totalPages, paged } = usePagination(filtered, 10);

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <PageHeader
        kicker="Satış Süreci"
        title="Teklifler"
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={() => navigate("/teklifler/yeni")} data-testid="new-quote-btn">
            <Plus size={16} className="mr-2" /> Yeni Teklif
          </Button>
        }
      />

      <Input placeholder="Ara: teklif no veya müşteri" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quotes-search"
        className="max-w-sm rounded-none border-2 border-[#09090B] focus-visible:ring-0" />

      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Teklif No</TableHead>
              <TableHead className="font-mono uppercase text-xs">Tarih</TableHead>
              <TableHead className="font-mono uppercase text-xs">Müşteri</TableHead>
              <TableHead className="font-mono uppercase text-xs">Ödeme</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">Ara Toplam</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">Toplam</TableHead>
              <TableHead className="font-mono uppercase text-xs">Durum</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((r) => (
              <TableRow
                key={r.id}
                data-testid={`quote-row-${r.id}`}
                onClick={() => setDetailId(r.id)}
                className="cursor-pointer hover:bg-neutral-50"
              >
                <TableCell className="font-mono font-bold">
                  {r.quote_number}
                  <div><SimilarQuoteBadge similar={r.similar} onOpen={() => setCompareId(r.id)} testid={`quote-similar-badge-${r.id}`} /></div>
                </TableCell>
                <TableCell className="text-sm">{fmtDate(r.created_at)}</TableCell>
                <TableCell className="font-semibold">{r.customer_name}</TableCell>
                <TableCell className="font-mono text-xs" data-testid={`quote-payment-${r.id}`}>
                  {r.payment_info
                    ? <span className="inline-block px-1.5 py-0.5 border border-neutral-400 text-neutral-700">{r.payment_info}</span>
                    : <span className="text-neutral-400">—</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-xs text-neutral-600" data-testid={`quote-subtotal-${r.id}`}>{fmtCurrency(r.subtotal)}</TableCell>
                <TableCell className="text-right font-mono">{fmtCurrency(r.total)}</TableCell>
                <TableCell>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border ${STATUS_COLORS[r.status] || "bg-neutral-100"}`} data-testid={`quote-status-${r.id}`}>
                    {STATUS_TR[r.status] || r.status}
                  </span>
                  {r.status === "converted" && r.order_cancelled_at && (
                    <span className="ml-1 inline-block px-2 py-0.5 text-[10px] font-mono uppercase border border-[#FF3B30] bg-[#FF3B30] text-white" data-testid={`quote-order-cancelled-${r.id}`}>
                      İPTAL
                    </span>
                  )}
                  {r.status === "converted" && r.order_status === "deleted" && (
                    <span className="ml-1 inline-block px-2 py-0.5 text-[10px] font-mono uppercase border border-neutral-500 text-neutral-500" data-testid={`quote-order-deleted-${r.id}`}>
                      SİPARİŞ SİLİNDİ
                    </span>
                  )}
                </TableCell>
                <TableCell
                  className="text-right whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button size="sm" variant="ghost" title="Detayı gör" onClick={() => setDetailId(r.id)} data-testid={`view-quote-${r.id}`}>
                    <Eye size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" title="PDF indir" onClick={() => downloadPdf(r)} data-testid={`quote-pdf-${r.id}`}>
                    <FileDown size={14} />
                  </Button>
                  {r.status !== "converted" && (
                    canManageQuote(r) ? (
                      <>
                        <Button size="sm" variant="ghost" title="Düzenle" onClick={() => navigate(`/teklifler/${r.id}`)} data-testid={`edit-quote-${r.id}`}><Pencil size={14} /></Button>
                        {r.status === "draft" && <Button size="sm" variant="ghost" title="Gönder" onClick={() => setStatus(r.id, "sent")}><Send size={14} /></Button>}
                        {r.status === "sent" && <Button size="sm" variant="ghost" title="Onayla" onClick={() => setStatus(r.id, "approved")}><Check size={14} className="text-green-700" /></Button>}
                        {r.status === "sent" && <Button size="sm" variant="ghost" title="Reddet" onClick={() => setStatus(r.id, "rejected")}><X size={14} className="text-red-700" /></Button>}
                        {(r.status === "approved" || r.status === "sent" || r.status === "draft") && (
                          <Button size="sm" variant="ghost" title="Siparişe dönüştür" onClick={() => convert(r.id)} data-testid={`convert-quote-${r.id}`}>
                            <ArrowRightCircle size={14} className="text-[#0055FF]" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" title="Sil" onClick={() => remove(r.id)} data-testid={`delete-quote-${r.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                      </>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-neutral-400"
                        title={`Yalnızca teklifi oluşturan (${r.created_by || "?"}) veya yönetici düzenleyebilir`}
                        data-testid={`quote-locked-${r.id}`}
                      >
                        <Lock size={10} /> {r.created_by || "-"}
                      </span>
                    )
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-neutral-500">Kayıt yok</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} setPage={setPage} totalPages={totalPages} testid="quotes-pager" />

      <SimilarQuotesDialog quoteId={compareId} onClose={() => setCompareId(null)} />
      <QuoteDetailsDialog
        quoteId={detailId}
        open={!!detailId}
        onOpenChange={(v) => !v && setDetailId(null)}
        onEdit={(qt) => navigate(`/teklifler/${qt.id}`)}
        onConvert={(qt, closeDialog, itemIds) => { closeDialog?.(); convert(qt.id, itemIds); }}
      />
    </div>
  );
}
